import { isValidAmount } from 'modules/core/lib/formatCurrencyUtils'

import getUserByItem from 'modules/payroll_bundle/components/common/helper/getUserByItem'
import type { SupplementType } from 'modules/payroll_bundle/components/common/helper/worksheet'
import type Supplement from 'modules/payroll_bundle/store/models/Supplement'

export const hasEmployeeAssigned = (supplement: Supplement): boolean => {
  return (
    getUserByItem(supplement) &&
    supplement.attributes.employee_id &&
    supplement.attributes.legal_entity_id
  )
}

// Checks if, for the specified supplementType, the legal entity of the
// employee assigned to this row (supplement) has been mapped to a taxonomy.
const hasTaxonomyForSupplementType = (
  supplement: Supplement,
  supplementType: SupplementType
): boolean => {
  return !!supplementType.legalEntityIdToTaxonomyId[supplement.attributes.legal_entity_id]
}

// If the amount for a supplement type is blank, no taxonomy is required
// (because no supplement will have to be created)
const hasTaxonomiesForRequiredSupplementTypes = (
  supplement: Supplement,
  supplementTypes: SupplementType[]
): boolean => {
  return supplementTypes.every((supplementType) => {
    const amount = supplementTypeAmount(supplement, supplementType)
    return !amount || hasTaxonomyForSupplementType(supplement, supplementType)
  })
}

const supplementTypeAmount = (supplement: Supplement, supplementType: SupplementType) => {
  return supplement.attributes[supplementType.id]?.trim()
}

const hasValidAmountForSupplementType = (
  supplement: Supplement,
  supplementType: SupplementType
): boolean => {
  const amount = supplementTypeAmount(supplement, supplementType)
  // Uses the fact that both undefined and the empty string are falsy
  return !amount || isValidAmount(amount)
}

const hasValidAmountsForAllSupplementTypes = (
  supplement: Supplement,
  supplementTypes: SupplementType[]
): boolean => {
  return supplementTypes.every((supplementType) =>
    hasValidAmountForSupplementType(supplement, supplementType)
  )
}

export const isValid = (supplement: Supplement, supplementTypes: SupplementType[]): boolean => {
  return (
    hasEmployeeAssigned(supplement) &&
    hasTaxonomiesForRequiredSupplementTypes(supplement, supplementTypes) &&
    hasValidAmountsForAllSupplementTypes(supplement, supplementTypes)
  )
}

export const countErrors = (supplement: Supplement, supplementTypes: SupplementType[]): number => {
  let errorCount = 0

  const missingEmployeeSelection = !hasEmployeeAssigned(supplement)
  if (missingEmployeeSelection) {
    errorCount += 1
  }

  return supplementTypes.reduce((count, supplementType) => {
    const amount = supplementTypeAmount(supplement, supplementType)

    if (!amount) {
      return count
    }

    let newCount = count
    if (!isValidAmount(amount)) {
      newCount += 1
    }
    if (!missingEmployeeSelection && !hasTaxonomyForSupplementType(supplement, supplementType)) {
      newCount += 1
    }
    return newCount
  }, errorCount)
}
